import { useState } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import {
  Box,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Button } from '@liminal/web-ui/primitives/Button';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Pagination } from '@liminal/web-ui/primitives/Pagination';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { usePushAnnouncements, useCancelPushAnnouncement } from '@/hooks/usePushAnnouncements';
import { pushAnnouncementStatusLabel, pushAnnouncementStatusVariant } from '@/components/ui/statusHelpers';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { formatOrderDate } from '@liminal/utils/date';
import type { AdminPushAnnouncement } from '@/api/types';
import { PushAnnouncementFormDialog } from './PushAnnouncementFormDialog';
import { describeAudience } from './describeAudience';
import { fontSizes } from '@liminal/brand/scale';
import { getErrorMessage } from '@/errors/messages';
import { PushAnnouncement_State } from '@/api/generated';

const PAGE_SIZE = 50;
const COLUMN_COUNT = 9;

/**
 * Sorts the page on screen — these page by offset and the endpoint takes no
 * sort parameter.
 */
const SORT_ACCESSORS = {
  title: (pa: AdminPushAnnouncement) => pa.title,
  message: (pa: AdminPushAnnouncement) => pa.message,
  status: (pa: AdminPushAnnouncement) => pushAnnouncementStatusLabel(pa.status),
  audience: (pa: AdminPushAnnouncement) =>
    describeAudience(pa.audienceKind, pa.audienceDescription, pa.audienceSize),
  sendAt: (pa: AdminPushAnnouncement) => pa.sendAt,
  recipientCount: (pa: AdminPushAnnouncement) => pa.recipientCount,
  createdBy: (pa: AdminPushAnnouncement) => pa.createdBy,
  createdAt: (pa: AdminPushAnnouncement) => pa.createdAt,
};

const PAGE_DEFAULTS = { offset: 0 };

export function PushAnnouncementsTab() {
  const [{ offset }, setParams] = useUrlState(PAGE_DEFAULTS);
  const [createOpen, setCreateOpen] = useState(false);
  // The push a new one is being seeded from, or null.
  const [copySource, setCopySource] = useState<AdminPushAnnouncement | null>(null);
  // The id of the scheduled push awaiting a cancel confirmation, or null.
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState('');

  const { data, isLoading, isError } = usePushAnnouncements({ limit: PAGE_SIZE, offset });
  const cancelMutation = useCancelPushAnnouncement();

  const total = data?.total ?? 0;

  // Newest first, as the endpoint returns them.
  const { rows: pushAnnouncements, sort } = useTableSort(
    data?.pushAnnouncements ?? [],
    SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelError('');
    try {
      await cancelMutation.mutateAsync(cancelTarget);
      setCancelTarget(null);
    } catch (e) {
      // Stays in the dialog: a failed cancel means the push is still going
      // out, and that has to be seen, not swallowed.
      setCancelError(getErrorMessage(e));
    }
  };

  return (
    <>
      <PageHeader
        title="Push Announcements"
        autoTitleCase={false}
        actions={
          <Button variant="primary" startIcon="plus" onClick={() => setCreateOpen(true)}>
            Send Push Announcement
          </Button>
        }
      />
      <Stack spacing={3}>

      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="title" sort={sort}>
                  Title
                </SortableTableCell>
                <SortableTableCell column="message" sort={sort}>
                  Message
                </SortableTableCell>
                <SortableTableCell column="status" sort={sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="audience" sort={sort}>
                  Audience
                </SortableTableCell>
                <SortableTableCell column="sendAt" sort={sort}>
                  Send At
                </SortableTableCell>
                <SortableTableCell column="recipientCount" sort={sort}>
                  Recipients
                </SortableTableCell>
                <SortableTableCell column="createdBy" sort={sort}>
                  Created By
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created
                </SortableTableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={pushAnnouncements.length === 0}
              loadingRows={8}
              emptyMessage="No push announcements yet."
              errorMessage="Could not load push announcements."
            >
              {pushAnnouncements.map((pa) => (
                      <TableRow key={pa.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {pa.title}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap title={pa.message}>
                            {pa.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusPill
                            label={pushAnnouncementStatusLabel(pa.status)}
                            tone={pushAnnouncementStatusVariant(pa.status)}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontSize: fontSizes.xs }}
                            title={describeAudience(pa.audienceKind, pa.audienceDescription, pa.audienceSize)}
                          >
                            {describeAudience(pa.audienceKind, pa.audienceDescription, pa.audienceSize)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                            {formatOrderDate(pa.sendAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {pa.recipientCount ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: fontSizes.xs }}>
                            {pa.createdBy}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                            {formatOrderDate(pa.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {pa.status === PushAnnouncement_State.PUSH_ANNOUNCEMENT_STATUS_SCHEDULED && (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={cancelMutation.isPending}
                                onClick={() => {
                                  setCancelError('');
                                  setCancelTarget(pa.id);
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                            <IconButton
                              icon="copy"
                              label={`Copy ${pa.title}`}
                              size="sm"
                              tone="muted"
                              onClick={() => setCopySource(pa)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Pagination
        offset={offset}
        pageSize={PAGE_SIZE}
        count={pushAnnouncements.length}
        total={total}
        onOffsetChange={(next) => setParams({ offset: next })}
        disabled={isLoading}
      />

      <PushAnnouncementFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      {/* Its own instance rather than a flag on the create dialog: `copyFrom`
          seeds state on open, so the two must not share one mount. */}
      <PushAnnouncementFormDialog
        open={copySource !== null}
        onClose={() => setCopySource(null)}
        copyFrom={copySource ?? undefined}
      />

      {/* Was a `window.confirm`. A native dialog cannot carry the brand, cannot
          show the cancel in flight, and cannot report a failure — and a failed
          cancel means the push is still going out. */}
      <ConfirmationDialog
        open={cancelTarget !== null}
        title="Cancel this scheduled push?"
        message="It has not been sent yet. Cancelling stops it going out to users."
        confirmLabel="Cancel push"
        cancelLabel="Keep it"
        destructive
        loading={cancelMutation.isPending}
        onConfirm={() => void handleCancelConfirm()}
        onCancel={() => {
          setCancelTarget(null);
          setCancelError('');
        }}
        data-testid="cancel-push-announcement"
      >
        <FormErrorBanner error={cancelError || undefined} />
      </ConfirmationDialog>
      </Stack>
    </>
  );
}
